/* eslint-disable max-classes-per-file */
import { MutableRefObject } from "react";
import {
    DashboardGroupBy,
    TicketState,
} from "../../__generated__/api";
import BaseListener from "./base-listener";
import { GroupBy } from "./group-by/base";
import { SortBy } from "./sort-by/base";
import TicketColumnGroupCache from "./ticket-column-group-cache";
import TicketColumnGroup from "./ticket-group";
import TicketItem from "./ticket-item";
import TicketColumnState from "./ticket-column-state";

const DEFAULT_GROUP_ID = "default";

export default class TicketColumn {
    private _groups: TicketColumnGroup[] = [];

    private _groupMap: Record<string, TicketColumnGroup> = Object.create(null);

    private _ticketCount: number | undefined;

    private _listener = new BaseListener();

    private _htmlContainer: MutableRefObject<HTMLDivElement | null> = {
        current: null,
    };

    private _updater = () => {
        this._listener.updateListners();
    };

    // eslint-disable-next-line no-useless-constructor
    public constructor(
        public readonly ticketState: TicketState,
        private readonly groupCache: TicketColumnGroupCache,
        private readonly groupBy: GroupBy | undefined,
        private readonly sortBy: SortBy | undefined,
        private readonly columnState: TicketColumnState,
    ) {
        // varitbles
    }

    public get ticketCount() {
        if (this._ticketCount != null) {
            return this._ticketCount;
        }
        this._ticketCount = this.groups.sum(group => group.length);
        return this._ticketCount;
    }

    public get groups(): TicketColumnGroup[] {
        return this._groups ?? [];
    }

    public removeTicket(ticket: TicketItem) {
        let groupId = DEFAULT_GROUP_ID;
        if (this.groupBy) {
            groupId = this.groupBy.getGroupId(ticket) ?? "";
        }
        const groupBy = this.groupBy?.groupBy ?? DashboardGroupBy.NoGroup;
        const group = this.groupCache.get(groupBy, this.ticketState.id, groupId);
        if (!group) {
            return;
        }
        group.removeTicket(ticket);
        if (group.length === 0) {
            group.listener.removeListener(this._updater);
            delete this._groupMap[groupId];
            const index = this._groups.findIndex(item => item.group?.id === group.group?.id);
            if (index >= 0) {
                this._groups.splice(index, 1);
            }
        }
    }

    public addTicket(ticket: TicketItem) {
        let groupId = DEFAULT_GROUP_ID;
        if (this.groupBy) {
            groupId = this.groupBy.getGroupId(ticket) ?? "";
        }
        const existingGroup = this._groupMap[groupId];
        if (existingGroup) {
            existingGroup.addTicket(ticket);
            return;
        }
        const groupBy = this.groupBy?.groupBy ?? DashboardGroupBy.NoGroup;
        let group = this.groupCache.get(groupBy, this.ticketState.id, groupId);
        if (!group) {
            group = new TicketColumnGroup(
                this.groupBy?.getGroup(groupId, [ticket]),
                this.sortBy,
                this,
                this.columnState,
            );
            this.groupCache.put(groupBy, this.ticketState.id, groupId, group);
        }
        group.setSorter(this.sortBy);
        group.clear();
        group.listener.addListener(this._updater);
        this._groupMap[groupId] = group;
        this._groups.push(group);
        group.addTicket(ticket);
    }

    public useDisplay() {
        this._listener.useListener();
        return this._htmlContainer;
    }

    public get unreadCount() {
        return this.groups.sum(group => group.tickets.count(ticket => ticket.isUnread));
    }

    public get unreadItems() {
        return this.groups.flatMap(group => group.tickets.filter(ticket => ticket.isUnread));
    }

    public get isVisible() {
        return this._htmlContainer.current?.classList.contains("visible") ?? false;
    }

    public scrollIntoView() {
        this._htmlContainer.current?.scrollIntoView({
            behavior: "smooth",
        });
    }
}
